import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { organizationSchema, getWebPageSchema, getBreadcrumbSchema, getFAQSchema } from '@/mocks/schemas';

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 韩漫网 - 免费韩国漫画在线阅读平台';
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <JsonLd schema={organizationSchema} />
        <JsonLd schema={getWebPageSchema(
          '关于我们 - 韩漫网 - 免费韩国漫画在线阅读平台',
          '了解韩漫网的品牌故事、用户口碑和联系方式。韩漫网致力于为中文读者提供优质的韩国漫画在线阅读体验。',
          'https://www.manhuakr.com/about'
        )} />
        <JsonLd schema={getBreadcrumbSchema([
          { name: '首页', item: 'https://www.manhuakr.com' },
          { name: '关于我们' },
        ])} />
        <JsonLd schema={getFAQSchema([
          { question: '韩漫网的漫画都是免费的吗？', answer: '是的！韩漫网所有漫画均提供免费在线阅读。我们会持续更新最新章节，让您无需付费即可畅享阅读体验。' },
          { question: '韩漫网多久更新一次？', answer: '热门连载漫画每日更新，您可以随时访问韩漫网查看最新章节。我们的编辑团队会第一时间同步发布新内容。' },
          { question: '如何在韩漫网找到想看的漫画？', answer: '您可以通过首页的分类导航浏览不同类型的漫画，也可以使用搜索功能直接查找您感兴趣的漫画作品。我们还提供编辑精选和热门推荐榜单，帮助您发现更多好作品。' },
          { question: '韩漫网支持手机阅读吗？', answer: '支持的！韩漫网采用响应式设计，完美适配电脑、平板和手机等各种设备。您可以在任何设备上流畅阅读您喜爱的韩漫。' },
          { question: '我可以推荐漫画给韩漫网吗？', answer: '当然可以！如果您有喜欢的韩漫希望我们引进，欢迎通过联系邮箱与我们沟通。我们非常重视读者的建议。' },
        ])} />

        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <header className="text-center py-10 md:py-14">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于韩漫网
            </h1>
            <p className="text-foreground-500 text-base max-w-xl mx-auto leading-relaxed">
              为中文读者打造的韩国漫画在线阅读平台，发现更多精彩作品
            </p>
          </header>

          <section className="mb-12">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                  韩漫网成立于2024年，是一个专注于韩国漫画（韩漫）在线阅读的中文平台。我们致力于为中文读者提供高质量的韩漫阅读体验，让更多读者能够轻松发现并享受韩国漫画的魅力。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm mb-4">
                  经过两年多的发展，韩漫网已经成长为国内领先的韩漫阅读平台之一。我们与多家韩国漫画平台和创作者建立了合作关系，拥有超过100部精选韩漫作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、BL等多种热门类型。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  我们的团队由一群热爱漫画的编辑和运营人员组成。我们每天精心筛选和推荐优质韩漫作品，确保每一位访问韩漫网的读者都能找到自己喜欢的漫画。我们的愿景是成为中文世界最受欢迎的韩漫阅读社区。
                </p>
              </div>
              <div className="flex-shrink-0 w-full sm:w-80">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20reading%20corner%20with%20bookshelves%20filled%20with%20Korean%20manhwa%20comic%20books%2C%20soft%20natural%20light%20through%20window%2C%20comfortable%20armchair%2C%20coffee%20cup%20on%20wooden%20side%20table%2C%20plants%2C%20minimalist%20aesthetic%2C%20calm%20atmosphere%2C%20photography%20style&width=600&height=400&seq=about-brand-01&orientation=landscape"
                    alt="韩漫网品牌形象"
                    title="韩漫网 - 韩国漫画在线阅读平台"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12 py-10 bg-background-100 rounded-2xl px-6 md:px-10">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-background-50 rounded-xl border border-secondary-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  "韩漫网的分类特别详细，找漫画特别方便。每天更新也很快，追更的首选平台！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center font-bold text-sm">L</div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm">李读者</p>
                    <p className="text-foreground-400 text-xs">使用韩漫网1年</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-50 rounded-xl border border-secondary-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  "推荐的都是高质量的韩漫！编辑推荐和热门榜单都很靠谱，发现了好多喜欢的作品。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center font-bold text-sm">W</div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm">王读者</p>
                    <p className="text-foreground-400 text-xs">使用韩漫网8个月</p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-background-50 rounded-xl border border-secondary-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  "终于找到一个专门看韩漫的中文网站了！界面清爽，阅读体验很流畅，强烈推荐！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-sm">Z</div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm">赵读者</p>
                    <p className="text-foreground-400 text-xs">使用韩漫网6个月</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-xl bg-background-50 border border-secondary-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-bold text-sm mb-1">正版资源</h3>
                <p className="text-foreground-400 text-xs">与韩国漫画平台合作，提供优质正版漫画内容</p>
              </div>

              <div className="p-5 rounded-xl bg-background-50 border border-secondary-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-speed-up-line text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-bold text-sm mb-1">每日更新</h3>
                <p className="text-foreground-400 text-xs">热门漫画每日同步更新，追更不停歇</p>
              </div>

              <div className="p-5 rounded-xl bg-background-50 border border-secondary-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-translate-2 text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-bold text-sm mb-1">中文翻译</h3>
                <p className="text-foreground-400 text-xs">专业翻译团队精心译制，阅读无障碍</p>
              </div>

              <div className="p-5 rounded-xl bg-background-50 border border-secondary-200/70 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-smartphone-line text-xl"></i>
                </div>
                <h3 className="text-foreground-900 font-bold text-sm mb-1">多端适配</h3>
                <p className="text-foreground-400 text-xs">电脑和手机均可流畅阅读，随心切换</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">联系我们</h2>
            <div className="max-w-lg mx-auto">
              <div className="p-6 bg-background-50 rounded-xl border border-secondary-200/70">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">电子邮箱</p>
                      <p className="text-foreground-900 font-medium text-sm">contact@manhuakr.com</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">微信公众号</p>
                      <p className="text-foreground-900 font-medium text-sm">韩漫网官方</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-global-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">官方网站</p>
                      <p className="text-foreground-900 font-medium text-sm">www.manhuakr.com</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">工作时间</p>
                      <p className="text-foreground-900 font-medium text-sm">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">常见问题</h2>
            <div className="max-w-2xl mx-auto space-y-3">
              {[
                { q: '韩漫网的漫画都是免费的吗？', a: '是的！韩漫网所有漫画均提供免费在线阅读。我们会持续更新最新章节，让您无需付费即可畅享阅读体验。' },
                { q: '韩漫网多久更新一次？', a: '热门连载漫画每日更新，您可以随时访问韩漫网查看最新章节。我们的编辑团队会第一时间同步发布新内容。' },
                { q: '如何在韩漫网找到想看的漫画？', a: '您可以通过首页的分类导航浏览不同类型的漫画，也可以使用搜索功能直接查找您感兴趣的漫画作品。我们还提供编辑精选和热门推荐榜单，帮助您发现更多好作品。' },
                { q: '韩漫网支持手机阅读吗？', a: '支持的！韩漫网采用响应式设计，完美适配电脑、平板和手机等各种设备。您可以在任何设备上流畅阅读您喜爱的韩漫。' },
                { q: '我可以推荐漫画给韩漫网吗？', a: '当然可以！如果您有喜欢的韩漫希望我们引进，欢迎通过联系邮箱与我们沟通。我们非常重视读者的建议。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-secondary-200/70 overflow-hidden">
                  <summary className="p-4 md:p-5 cursor-pointer flex items-center justify-between">
                    <span className="text-foreground-900 font-medium text-sm pr-4">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}